import { passwordSchema } from '@nebueye/shared'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field, FormAlert } from '@/components/app/form'
import { Button } from '@/components/ui/button'
import { authClient, authErrorMessage } from '@/lib/auth-client'

export const Route = createFileRoute('/_auth/reset-password')({
  validateSearch: z.object({ token: z.string().optional(), error: z.string().optional() }),
  head: () => ({ meta: [{ title: 'Choose a new password · Nebueye' }] }),
  component: ResetPassword,
})

function ResetPassword() {
  const { token, error: linkError } = Route.useSearch()
  const navigate = useNavigate()
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)

  if (!token || linkError) {
    return (
      <div className="space-y-5">
        <h1 className="text-headline font-semibold">This link has expired</h1>
        <p className="text-muted-foreground text-[15px]">Reset links work once and last an hour. Request a fresh one.</p>
        <Button asChild>
          <Link to="/forgot-password">Send a new link</Link>
        </Button>
      </div>
    )
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const parsed = passwordSchema.safeParse(new FormData(e.currentTarget).get('password'))
    if (!parsed.success) return setError(parsed.error.issues[0]?.message)
    setPending(true)
    const { error } = await authClient.resetPassword({ newPassword: parsed.data, token })
    setPending(false)
    if (error) return setError(authErrorMessage(error))
    toast.success('Password updated. Sign in with your new password.')
    navigate({ to: '/sign-in' })
  }

  return (
    <div className="space-y-7">
      <div className="space-y-2">
        <h1 className="text-headline font-semibold">Choose a new password</h1>
        <p className="text-muted-foreground text-[15px]">You'll be signed out on your other devices.</p>
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <FormAlert>{error}</FormAlert>}
        <Field label="New password" name="password" type="password" autoComplete="new-password" hint="At least 10 characters." autoFocus />
        <Button type="submit" size="lg" className="w-full" disabled={pending}>
          {pending && <Loader2 className="animate-spin" />}
          Update password
        </Button>
      </form>
    </div>
  )
}
